/**
 * ClientSummaryHeader Component
 *
 * Feature: 126-client-assignment-views (T014)
 * Description: The summary strip under the Client Detail page's title: capacity count,
 *   workspace count, managed data, created date, and the SLA tier and contract value.
 *   The name, code, status and back link live in the page's DetailLayout (Spec 226 T035).
 * Constitution: v1.12.0 — Principle XXXVII (Code Header Standards)
 */

import React from 'react';
import { Cpu, FolderOpen, Calendar, Database } from 'lucide-react';
import type { ClientDetail } from '@shared/types/client-detail';
import { formatBytes } from '../../utils/formatBytes';
import { formatEasternDate } from '@/utils/dateFormatter';
import { UnmeasuredSizeTag } from '@/utils/unmeasuredSize';
import { Card } from '../ui';

interface ClientSummaryHeaderProps {
  client: ClientDetail;
}

export const ClientSummaryHeader: React.FC<ClientSummaryHeaderProps> = ({ client }) => {
  // Format created date
  const formatDate = (dateString: string): string => {
    return formatEasternDate(dateString);
  };

  return (
    <Card padding="lg" className="mb-6">
      {/* Counts and date */}
      <div className="flex flex-wrap items-center gap-6 text-sm text-[var(--color-text-secondary)]">
        {/* Capacity count */}
        <div className="flex items-center gap-2">
          <Cpu className="w-5 h-5 text-[var(--color-text-secondary)]" />
          <div>
            <span className="text-lg font-semibold text-[var(--color-text-primary)]">
              {client.capacityCount}
            </span>
            <span className="ml-1">
              {client.capacityCount === 1 ? 'capacity' : 'capacities'}
            </span>
          </div>
        </div>

        {/* Workspace count */}
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-[var(--color-text-secondary)]" />
          <div>
            <span className="text-lg font-semibold text-[var(--color-text-primary)]">
              {client.workspaceCount}
            </span>
            <span className="ml-1">
              {client.workspaceCount === 1 ? 'workspace' : 'workspaces'}
            </span>
          </div>
        </div>

        {/* Managed data rollup (Spec 157 US4) */}
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-[var(--color-text-secondary)]" />
          <div>
            <span className="text-lg font-semibold text-[var(--color-text-primary)]">
              {formatBytes(client.totalSizeBytes ?? null)}
            </span>
            <span className="ml-1">managed</span>
            {/* Spec 286 T159 item 32: the total keeps sizes the last collection did not measure. */}
            <UnmeasuredSizeTag count={client.sizeNotMeasuredCount} />
          </div>
        </div>

        {/* Created date */}
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[var(--color-text-secondary)]" />
          <span>Created {formatDate(client.createdAt)}</span>
        </div>
      </div>

      {/* SLA and contract info row (optional) */}
      {(client.slaTier || client.contractValue) && (
        <div className="mt-4 pt-4 border-t border-[var(--color-border)] flex items-center gap-6 text-sm">
          {client.slaTier && (
            <div>
              <span className="text-[var(--color-text-secondary)]">SLA Tier:</span>
              <span className="ml-2 font-medium text-[var(--color-text-primary)]">
                {client.slaTier}
              </span>
            </div>
          )}
          {client.contractValue && (
            <div>
              <span className="text-[var(--color-text-secondary)]">Contract Value:</span>
              <span className="ml-2 font-medium text-[var(--color-text-primary)]">
                ${client.contractValue.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default ClientSummaryHeader;
